import { PayloadAction } from '@reduxjs/toolkit'
import { actions as appActions } from '../state'
import { toast } from 'react-toastify'
import { ReturnCall } from '../types'
import { actions } from './ShedManagementState'
import {
  FiltersSearchLocations,
  GetFiltersSearchLocationsPayload,
  GetLocationsPayload,
  GetProductsPayload,
  GetShedsSearchLocationsPayload,
  Product,
  SelectedLocations,
  CreateLocationsByCsvPayload,
} from './ShedManagementTypes'
import {
  all,
  call,
  put,
  takeLatest,
} from 'redux-saga/effects'
import apis from './ShedManagementApi'

export function* getAttributesGroup() {
  try {
    yield put(appActions.isLoading(true))
    yield put(actions.clearState())
    const attributesGroup: ReturnCall<typeof apis.getAttributesGroup> = yield call(
      apis.getAttributesGroup,
    )

    if (!attributesGroup.isOk) {
      toast.error(attributesGroup?.data?.data?.message || 'Não foi possível obter os grupos de atributos!')
      return
    }
    yield put(actions.setAttributesGroup({ attributesGroup: attributesGroup.data }))

    const locationHeightTypes: ReturnCall<typeof apis.getLocationHeightTypes> = yield call(
      apis.getLocationHeightTypes,
    )

    if (!locationHeightTypes.isOk) {
      toast.error(locationHeightTypes?.data?.data?.message || 'Não foi possível obter os tipos de alturas!')
      return
    }
    yield put(actions.setLocationHeightTypes({ locationHeightTypes: locationHeightTypes.data }))
  } catch (error) {
    toast.error('Não foi possível obter os grupos de atributos!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getCreateFilters() {
  try {
    yield put(appActions.isLoading(true))
    const createFiltersData: ReturnCall<typeof apis.getCreateFilters> = yield call(
      apis.getCreateFilters,
    )

    if (!createFiltersData.isOk) {
      toast.error(createFiltersData?.data?.data?.message || 'Não foi possível obter os filtros de criação!')
      return
    }
    yield put(actions.setCreateFiltersData({ createFiltersData: createFiltersData.data }))
  } catch (error) {
    toast.error('Não foi possível obter os filtros de criação!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* createLocationsByCsv(
  data: PayloadAction<CreateLocationsByCsvPayload>,
) {
  try {
    yield put(appActions.isLoading(true))
    yield put(actions.setCsvValidationErrors({ csvValidationErrors: [] }))

    const result: ReturnCall<typeof apis.createLocationsByCsv> = yield call(
      apis.createLocationsByCsv,
      data.payload,
    )

    if (!result.isOk) {
      if (result.data?.data?.errors && Array.isArray(result.data.data.errors)) {
        yield put(actions.setCsvValidationErrors({
          csvValidationErrors: result.data.data.errors,
        }))
        toast.error('Arquivo CSV contém erros de validação. Verifique a tabela abaixo.')
      } else {
        toast.error(result?.data?.data?.message || 'Não foi possível criar as localizações!')
      }
      return
    }

    yield put(actions.setCsvValidationErrors({ csvValidationErrors: [] }))
    toast.success('Localizações criadas com sucesso!')
  } catch (error) {
    yield put(actions.setCsvValidationErrors({ csvValidationErrors: [] }))
    toast.error('Não foi possível criar as localizações!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getShedsSearchLocations(
  data: PayloadAction<GetShedsSearchLocationsPayload>,
) {
  try {
    const result: ReturnCall<typeof apis.getShedsSearchLocations> = yield call(
      apis.getShedsSearchLocations,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível obter os galpões!')
      return
    }
    yield put(actions.setShedsSearchLocations({ shedsSearchLocations: result.data }))
  } catch (error) {
    toast.error('Não foi possível obter os galpões!')
  }
}

export function* getFiltersSearchLocations(
  data: PayloadAction<GetFiltersSearchLocationsPayload>,
) {
  try {
    yield put(actions.addRequestsSearchingFilters())

    const result: ReturnCall<typeof apis.getFiltersSearchLocations> = yield call(
      apis.getFiltersSearchLocations,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível obter os filtros!')
      return
    }

    const filterSearchLocations: FiltersSearchLocations = result.data

    const customSort = (a: string, b: string): number => {
      if (a > b) {
        return 1
      }

      if (a < b) {
        return -1
      }

      return 0
    }

    filterSearchLocations.shelves.sort(customSort)
    filterSearchLocations.bins.sort(customSort)

    yield put(actions.setFilterSearchLocations({ filterSearchLocations }))
  } catch (error) {
    toast.error('Não foi possível obter os filtros!')
  } finally {
    yield put(actions.removeRequestsSearchingFilters())
  }
}

export function* getLocations(
  data: PayloadAction<GetLocationsPayload>,
) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getLocations> = yield call(
      apis.getLocations,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível obter as localizações!')
      return
    }
    yield put(actions.setLocationAttributeDiscrepancyErrors({ locationAttributeDiscrepancyErrors: [] }))
    yield put(actions.setSelectedLocations({ selectedLocations: [] }))
    yield put(actions.setLocations({ locations: result.data }))
  } catch (error) {
    toast.error('Não foi possível obter as localizações!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* saveAttributesLocations(
  data: PayloadAction<SelectedLocations[]>,
) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.saveAttributesLocations> = yield call(
      apis.saveAttributesLocations,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível salvar as alterações!')
      return
    }

    const customData: any = result.data
    if (customData.locations.length === 0 && customData.error) {
      toast.error('Algo divergente, consulte o log de erros!')
      yield put(actions.setLocationAttributeDiscrepancyErrors({ locationAttributeDiscrepancyErrors: customData.error.errors }))
    } else if (customData.locations.length > 0 && customData.error) {
      toast.info('Configurações realizadas com divergências em uma ou mais localizações, consulte o log de erros!')
      yield put(actions.setLocationAttributeDiscrepancyErrors({ locationAttributeDiscrepancyErrors: customData.error.errors }))
    } else {
      yield put(actions.setLocationAttributeDiscrepancyErrors({ locationAttributeDiscrepancyErrors: [] }))
      toast.success('Configurações realizadas com sucesso!')
    }

    yield put(actions.updateLocations(customData.locations))
  } catch (error) {
    toast.error('Não foi possível salvar as alterações!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* getProducts(
  data: PayloadAction<GetProductsPayload>,
) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.getProducts> = yield call(
      apis.getProducts,
      data.payload,
    )

    if (!result.isOk) {
      toast.error(result?.data?.data?.message || 'Não foi possível obter as produtos!')
      return
    }
    yield put(actions.setProductAttributeDiscrepancyErrors({ productAttributeDiscrepancyErrors: [] }))
    yield put(actions.setSelectedProducts({ selectedProducts: [] }))
    yield put(actions.setProducts(result.data))
  } catch (error) {
    toast.error('Não foi possível obter as produtos!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export function* saveAttributesProducts(
  data: PayloadAction<Product[]>,
) {
  yield put(appActions.isLoading(true))

  try {
    const result: ReturnCall<typeof apis.saveAttributesProducts> = yield call(
      apis.saveAttributesProducts,
      data.payload,
    )

    if (!result.isOk) {
      const errorStatusBadRequest = 400
      const errorCode = 'PRODUCT_ATTRIBUTE_ERROR'

      if (result.data.status === errorStatusBadRequest && result.data.data.errorCode === errorCode) {
        toast.error('Algo divergente, consulte o log de erros!')
        yield put(actions.setProductAttributeDiscrepancyErrors({ productAttributeDiscrepancyErrors: result.data.data.errors }))
        return
      }
      toast.error(result?.data?.data?.message || 'Não foi possível salvar as alterações!')
      return
    }
    yield put(actions.updateProducts(result.data))
    yield put(actions.setProductAttributeDiscrepancyErrors({ productAttributeDiscrepancyErrors: [] }))
    toast.success('Configurações realizadas com sucesso!')
  } catch (error) {
    toast.error('Não foi possível salvar as alterações!')
  } finally {
    yield put(appActions.isLoading(false))
  }
}

export default function* () {
  yield all([
    takeLatest(actions.getAttributesGroup.type, getAttributesGroup),
    takeLatest(actions.getShedsSearchLocations.type, getShedsSearchLocations),
    takeLatest(actions.getFiltersSearchLocations.type, getFiltersSearchLocations),
    takeLatest(actions.getLocations.type, getLocations),
    takeLatest(actions.saveAttributesLocations.type, saveAttributesLocations),
    takeLatest(actions.getProducts.type, getProducts),
    takeLatest(actions.saveAttributesProducts.type, saveAttributesProducts),
    takeLatest(actions.getCreateFilters.type, getCreateFilters),
    takeLatest(actions.createLocationsByCsv.type, createLocationsByCsv),
  ])
}
